import { type RefObject, useEffect, useRef, useState } from 'react'

import { isEnterKey } from '~/libs/keyboard'
import {
  type CellPos,
  type CellRange,
  type GridBounds,
  makeRange,
  rangeToTsv,
  stepCell,
} from '~/models/gridSelection'

/** 値のセルへの書き込み (row / col は表の中の位置)。 */
export type GridCellUpdate = { row: number; col: number; value: string }

type Options = {
  bounds: GridBounds
  /** 最初に選んでおくセル。 */
  initialCell: CellPos
  /** 編集できる状態か。 false の間も選択・移動・コピーはできるが、 値は変えられない。 既定は true。 */
  isEditable?: boolean
  /** 値を変えられるセルか (見出しや時刻の列は選択とコピーだけ)。 */
  isValueCell: (row: number, col: number) => boolean
  /** 値のセルが持っている文字 (編集はここから始まる)。 */
  valueAt: (row: number, col: number) => string
  /** セルに見える文字 (コピーもこの文字を写す)。 */
  cellText: (row: number, col: number) => string
  /** 値のセルが書き換わった (編集の確定・Delete・貼り付け)。 */
  onCellsChange: (updates: GridCellUpdate[]) => void
}

type Editing = { row: number; col: number; draft: string }

/** `useExcelGridEditing` が返す、 表の状態と操作。 */
export type ExcelGridEditing = {
  rootRef: RefObject<HTMLDivElement | null>
  anchor: CellPos
  focus: CellPos
  range: CellRange
  /** 編集中のセル (無ければ null) と、 入力途中の文字。 */
  editing: Editing | null
  setAnchor: (pos: CellPos) => void
  setFocus: (pos: CellPos) => void
  /** 値のセルを書き換える (編集できない状態では何もしない)。 貼り付けはこれで入れる。 */
  setCells: (updates: GridCellUpdate[]) => void
  startEdit: (row: number, col: number, initial?: string) => void
  valueCellsIn: (r: CellRange) => CellPos[]
  handleKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void
  handleCopy: (e: React.ClipboardEvent<HTMLDivElement>) => void
  cellMouseHandlers: (
    row: number,
    col: number,
  ) => Pick<
    React.HTMLAttributes<HTMLTableCellElement>,
    'onMouseDown' | 'onMouseEnter'
  >
  editInputHandlers: (
    row: number,
    col: number,
  ) => Pick<
    React.InputHTMLAttributes<HTMLInputElement>,
    'onBlur' | 'onChange' | 'onKeyDown'
  >
}

/**
 * Excel 風の表 (セルのクリック選択・ドラッグ範囲選択・矢印キー移動 (Shift で範囲拡張)・
 * F2 / Enter / 文字入力で編集開始・Enter で確定して下へ・Esc で取消・Ctrl+C で選んだ範囲を TSV コピー・
 * Delete で選択範囲を空にする) の状態遷移とキー処理。 表の見た目・貼り付けの計画・値の持ち方は
 * 表ごとに違うので呼び出し側が持つ。 貼り付けは `setCells` / `setAnchor` / `setFocus` で行う。
 *
 * 日本語 IME の変換中の Enter / Esc はセル操作にしない。
 */
export function useExcelGridEditing({
  bounds,
  initialCell,
  isEditable = true,
  isValueCell,
  valueAt,
  cellText,
  onCellsChange,
}: Options): ExcelGridEditing {
  const rootRef = useRef<HTMLDivElement>(null)
  const dragging = useRef(false)
  const [anchor, setAnchor] = useState<CellPos>(initialCell)
  const [focus, setFocus] = useState<CellPos>(initialCell)
  const [editing, setEditingState] = useState<Editing | null>(null)
  // Enter / Esc で input が外れる時の blur が、 確定済みの編集をもう一度確定しないよう
  // 最新の編集状態は ref からも読む。
  const editingRef = useRef(editing)
  const setEditing = (next: Editing | null): void => {
    editingRef.current = next
    setEditingState(next)
  }
  const range: CellRange = makeRange(anchor, focus)

  useEffect(() => {
    const stop = (): void => {
      dragging.current = false
    }
    window.addEventListener('mouseup', stop)
    return () => {
      window.removeEventListener('mouseup', stop)
    }
  }, [])

  // 編集できない状態になったら、 編集中の入力は捨てる (確定した値は呼び出し側に渡っている)
  useEffect(() => {
    if (!isEditable) {
      editingRef.current = null
      setEditingState(null)
    }
  }, [isEditable])

  // 表示範囲が狭まった時 (月全体 → 1 日など) は、 範囲外に残った選択を範囲内へ戻し、
  // 範囲外の編集は捨てる。 範囲外の位置のまま書き込むと、 呼び出し側の配列の外に値が入る。
  const { rowCount, colCount } = bounds
  useEffect(() => {
    const clamp = (pos: CellPos): CellPos => ({
      row: Math.min(pos.row, rowCount - 1),
      col: Math.min(pos.col, colCount - 1),
    })
    const outside = (pos: CellPos): boolean =>
      pos.row >= rowCount || pos.col >= colCount
    setAnchor((prev) => (outside(prev) ? clamp(prev) : prev))
    setFocus((prev) => (outside(prev) ? clamp(prev) : prev))
    if (editingRef.current !== null && outside(editingRef.current)) {
      editingRef.current = null
      setEditingState(null)
    }
  }, [rowCount, colCount])

  const select = (pos: CellPos, extend: boolean): void => {
    setFocus(pos)
    if (!extend) setAnchor(pos)
  }

  const setCells = (updates: GridCellUpdate[]): void => {
    const inBounds = updates.filter(
      ({ row, col }) => row < bounds.rowCount && col < bounds.colCount,
    )
    if (inBounds.length === 0 || !isEditable) return
    onCellsChange(inBounds)
  }

  const startEdit = (row: number, col: number, initial?: string): void => {
    if (!isEditable || !isValueCell(row, col)) return
    setEditing({ row, col, draft: initial ?? valueAt(row, col) })
  }

  const finishEdit = (commit: boolean, moveDown: boolean): void => {
    const current = editingRef.current
    if (current === null) return
    if (commit) {
      setCells([{ row: current.row, col: current.col, value: current.draft }])
    }
    setEditing(null)
    if (moveDown) {
      const next = stepCell(
        { row: current.row, col: current.col },
        1,
        0,
        bounds,
      )
      select(next ?? { row: current.row, col: current.col }, false)
    }
    rootRef.current?.focus()
  }

  // 編集中の Esc は編集の取消。モーダル (Zag Dialog) は Esc を document の capture で受けるので、
  // セルの onKeyDown では先にモーダルが閉じてしまう。window の capture で先に受けて止める
  // (useDropdownDismiss と同じ方式)。IME の変換中の Esc は変換の取消なので取消にしない
  const hasEdit = editing !== null
  const finishEditRef = useRef(finishEdit)
  finishEditRef.current = finishEdit
  useEffect(() => {
    if (!hasEdit) return
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      if (e.isComposing || e.keyCode === 229) return
      e.preventDefault()
      finishEditRef.current(false, false)
    }
    window.addEventListener('keydown', handleKeyDown, true)
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [hasEdit])

  /** 範囲の中の値のセルを並べる (見出し・時刻の列は飛ばす)。 */
  const valueCellsIn = (r: CellRange): CellPos[] => {
    const cells: CellPos[] = []
    for (let row = r.rowMin; row <= r.rowMax; row++) {
      for (let col = r.colMin; col <= r.colMax; col++) {
        if (isValueCell(row, col)) cells.push({ row, col })
      }
    }
    return cells
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (editing !== null || e.ctrlKey || e.metaKey) return
    const arrows: Record<string, [number, number]> = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    }
    const delta = arrows[e.key]
    if (delta !== undefined) {
      e.preventDefault()
      const next = stepCell(focus, delta[0], delta[1], bounds) ?? focus
      select(next, e.shiftKey)
      return
    }
    if (!isEditable) return
    if (e.key === 'F2' || isEnterKey(e)) {
      e.preventDefault()
      startEdit(focus.row, focus.col)
      return
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault()
      setCells(
        valueCellsIn(range).map(({ row, col }) => ({ row, col, value: '' })),
      )
      return
    }
    if (e.key.length === 1 && !e.altKey) {
      e.preventDefault()
      startEdit(focus.row, focus.col, e.key)
    }
  }

  const handleCopy = (e: React.ClipboardEvent<HTMLDivElement>): void => {
    if (editing !== null) return
    e.preventDefault()
    e.clipboardData.setData('text/plain', rangeToTsv(range, cellText))
  }

  /** セル共通の選択の配線 (クリック・ドラッグ)。 見出しも値も同じ操作で選べる。 */
  const cellMouseHandlers = (
    row: number,
    col: number,
  ): Pick<
    React.HTMLAttributes<HTMLTableCellElement>,
    'onMouseDown' | 'onMouseEnter'
  > => ({
    onMouseDown: (e) => {
      if (editing?.row === row && editing.col === col) return
      if (editing !== null) finishEdit(true, false)
      dragging.current = true
      select({ row, col }, e.shiftKey)
      rootRef.current?.focus()
    },
    onMouseEnter: () => {
      if (dragging.current) setFocus({ row, col })
    },
  })

  /** 編集中のセルの input に付ける操作。 blur で確定し、 Enter で確定して下へ、 Esc で取消。 */
  const editInputHandlers = (
    row: number,
    col: number,
  ): Pick<
    React.InputHTMLAttributes<HTMLInputElement>,
    'onBlur' | 'onChange' | 'onKeyDown'
  > => ({
    onBlur: () => {
      finishEdit(true, false)
    },
    onChange: (e) => {
      setEditing({ row, col, draft: e.target.value })
    },
    onKeyDown: (e) => {
      e.stopPropagation()
      // 日本語 IME の変換中の Enter (変換確定) / Esc (変換取消) はセル操作にしない
      if (e.nativeEvent.isComposing || e.keyCode === 229) return
      if (isEnterKey(e)) {
        e.preventDefault()
        finishEdit(true, true)
      } else if (e.key === 'Escape') {
        e.preventDefault()
        finishEdit(false, false)
      }
    },
  })

  return {
    rootRef,
    anchor,
    focus,
    range,
    editing,
    setAnchor,
    setFocus,
    setCells,
    startEdit,
    valueCellsIn,
    handleKeyDown,
    handleCopy,
    cellMouseHandlers,
    editInputHandlers,
  }
}
